import { env } from 'configs/EnvironmentConfig'
import fetch from 'auth/FetchInterceptor'
import { z } from 'zod'
import { Validate } from './SchemaValidation'

const VpsService = {}

const uuid = z.string().uuid()
const numberId = z.number()

VpsService.getVpsRemote = id =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/vps/${id}/remote`,
    method: 'GET',
  })

VpsService.getVncPassword = id =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/vps/${id}/vnc_password`,
    method: 'GET',
  })

VpsService.listTemplates = () =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/template`,
    method: 'GET',
  })

VpsService.listDatacenters = () =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/datacenter`,
    method: 'GET',
  })

VpsService.listNodes = () =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/node`,
    method: 'GET',
  })

VpsService.listGetNode = id =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/node/${Validate(numberId, id)}`,
    method: 'GET',
  })

VpsService.listCatalog = () =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/catalog`,
    method: 'GET',
  })

VpsService.listVps = () =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/vps`,
    method: 'GET',
  })

VpsService.getIpProviders = () =>
  fetch({
    url: `${env.IPS_ENDPOINT_URL}/api/provider`,
    method: 'GET',
  })

VpsService.getVps = vpsId =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/vps/${Validate(uuid, vpsId)}`,
    method: 'GET',
  })

VpsService.getSettings = () =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/settings/me`,
    method: 'GET',
  })

VpsService.startVps = vpsId =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/vps/${Validate(uuid, vpsId)}/start`,
    method: 'PATCH',
  })

VpsService.stopVps = vpsId =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/vps/${Validate(uuid, vpsId)}/stop`,
    method: 'PATCH',
  })

VpsService.restartVps = vpsId =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/vps/${Validate(uuid, vpsId)}/reboot`,
    method: 'PATCH',
  })

const rebuildVpsSchema = z
  .object({
    root_password: z.string(),
    template_id: z.number().optional(),
  })
  .partial()

VpsService.rebuildVps = rawData => {
  const { id, ...data } = rawData

  return fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/vps/${Validate(uuid, id)}/rebuild`,
    method: 'POST',
    data: Validate(rebuildVpsSchema, data),
  })
}

VpsService.shutdownVps = vpsId =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/vps/${Validate(uuid, vpsId)}/shutdown`,
    method: 'PATCH',
  })

VpsService.deleteVps = vpsId =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/vps/${Validate(uuid, vpsId)}`,
    method: 'DELETE',
  })

const postVpsSchema = z
  .object({
    name: z.string().regex(/^[a-zA-Z-0-9]+[a-zA-Z-0-9.]*[a-zA-Z-0-9]$/),
    template_id: z.number().int(),
    node_id: z.number().int(),
    vps_catalog_id: z.number().int(),
    ssh_key_ids: z.array(z.number().int()).nullable(),
    root_password: z.string().nullable(),
    ssh_keys: z.string().nullable(),
    vps_plan_type_id: z.literal(1), // set vps type plan to 1
    vcpu: z.number(),
    memory: z.number(),
    ipv4: z.boolean(),
    ipv6: z.boolean(),
    disk_size: z.number(),
    addons: z.array(z.number().int()),
    provider_id: z.number().int().optional(),
  })
  .partial()

VpsService.postVps = data =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/vps`,
    method: 'POST',
    data: Validate(postVpsSchema, data),
  })

const postSshSchema = z.object({
  name: z.string(),
  key: z.string(),
})

const postResizeSchema = z.object({
  vcpu: z.number().optional(),
  memory: z.number().optional(),
  disk_size: z.number().optional(),
  ipv4: z.boolean().optional(),
})
VpsService.resizeVps = (id, data) =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/vps/${id}/resize`,
    method: 'POST',
    data: Validate(postResizeSchema, data),
  })

VpsService.postSsh = data =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/ssh`,
    method: 'POST',
    data: Validate(postSshSchema, data),
  })

VpsService.editSsh = (id, data) =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/ssh/${id}`,
    method: 'PUT',
    data: Validate(postSshSchema, data),
  })

VpsService.deleteSsh = id =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/ssh/${Validate(numberId, id)}`,
    method: 'DELETE',
  })

VpsService.listSshKeys = () =>
  fetch({
    url: `${env.VPS_ENDPOINT_URL}/api/ssh`,
    method: 'GET',
  })

export default VpsService
